import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';

import { orderBy } from 'lodash';

import { CompaniesService } from '../companies/companies.service';
import { CustomHttpResponse } from '../../classes/http';
import { BranchNames, CityNames, DistrictCoefficient, DistrictNames, SectorNames, SectorRole, Sectors } from './sectors';
import { StreetType } from '../companies/companies';
import { map } from 'rxjs/operators';

@Injectable()
export class SectorsService {
  constructor(
    private http: HttpClient,
    private companiesService: CompaniesService
  ) {}

  getPlots(params): Observable<CustomHttpResponse<Sectors>> {
    return this.http.get<CustomHttpResponse<Sectors>>(`/admin/sectors/`, {
      params: params,
    });
  }

  getFilterPlots(params): Observable<CustomHttpResponse<SectorNames[]>> {
    return this.http.get<CustomHttpResponse<SectorNames[]>>(`/admin/sector_numbers`, {
      params: params,
    });
  }

  createOrUpdateSector(params): Observable<any> {
    return this.http.post(`/admin/sectors/`, params);
  }

  updatePlotStatus(id, data): Observable<any> {
    return this.http.patch(`/admin/sectors/${id}/`, data);
  }

  getRegions(): Observable<CustomHttpResponse<string[]>> {
    return this.http.get<CustomHttpResponse<string[]>>(`/admin/regions`);
  }

  getBranches(params): Observable<CustomHttpResponse<BranchNames[]>> {
    return this.http.get<CustomHttpResponse<BranchNames[]>>(`/admin/branches/`, { params });
  }

  getCities(params): Observable<CustomHttpResponse<CityNames[]>> {
    return this.http.get<CustomHttpResponse<CityNames[]>>(`/admin/cities/`, {
      params,
    });
  }

  getCitiesNames(params = {}): Observable<CustomHttpResponse<string[]>> {
    return this.http.get<CustomHttpResponse<string[]>>('/admin/cities/names/', { params });
  }

  getStreetTypes(): Observable<CustomHttpResponse<StreetType[]>> {
    return this.http.get<CustomHttpResponse<StreetType[]>>(`/admin/street_types/`);
  }

  getResponsible(params): Observable<any> {
    return this.http.get(`/admin/employees/`, { params });
  }

  getDistricts(params): Observable<CustomHttpResponse<DistrictNames[]>> {
    return this.http.get<CustomHttpResponse<DistrictNames[]>>(`/admin/districts/`, { params });
  }

  getDistrictsAvailable(params): Observable<any> {
    return this.http.get(`/admin/districts/available/`, { params });
  }

  getDistrictCoefficients(districtId: number): Observable<CustomHttpResponse<DistrictCoefficient[] | { results: DistrictCoefficient[] }>> {
    return this.http.get<CustomHttpResponse<DistrictCoefficient[] | { results: DistrictCoefficient[] }>>('/admin/district_coefficients/', {
      params: { district_id: districtId },
    });
  }

  createDistrictCoefficient(districtId: number, coefficient: string): Observable<CustomHttpResponse<DistrictCoefficient>> {
    return this.http.post<CustomHttpResponse<DistrictCoefficient>>('/admin/district_coefficients/', {
      district_id: districtId,
      coefficient,
    });
  }

  updateDistrictCoefficient(id: number, coefficient: string): Observable<CustomHttpResponse<DistrictCoefficient>> {
    return this.http.patch<CustomHttpResponse<DistrictCoefficient>>(`/admin/district_coefficients/${id}/`, { coefficient });
  }

  getVersions(id): Observable<any> {
    return this.http.get(`/admin/sectors/${id}/all_versions/`);
  }

  getSectorNumber(id): Observable<any> {
    return this.http.get(`/admin/branches/${id}`);
  }

  getEmployersUnbind(id): Observable<any> {
    return this.http.get(`/admin/sectors/${id}/users/`);
  }

  getCreateEditExpandedData(mb_id, sector_id, sector_role): Observable<any> {
    const districtsParams = {
      mb_id,
      free_or_attached_to: sector_id,
      sector_role,
    };
    const districts = this.getDistrictsAvailable(districtsParams);
    const sectorNumbers = this.getSectorNumber(mb_id);

    return forkJoin([districts, sectorNumbers]);
  }

  getCreatePlotUnion(): Observable<any> {
    const regions = this.getRegions();
    const streetTypes = this.getStreetTypes();
    const citiesNames = this.getCitiesNames();
    const companies = this.companiesService.getCompaniesFilter();

    return forkJoin([regions, streetTypes, citiesNames, companies]);
  }

  getEditPlotUnion(id): Observable<any> {
    const regions = this.getRegions();
    const streetTypes = this.getStreetTypes();
    const citiesNames = this.getCitiesNames();
    const versions = this.getVersions(id);
    const companies = this.companiesService.getCompaniesFilter();

    return forkJoin([regions, streetTypes, citiesNames, versions, companies]);
  }

  getFilterData(params): Observable<any> {
    const branchesParams: any = {};
    const citiesParams: any = {};
    const plotsParams: any = {};
    const districtsParams: any = {};

    if (params.mb_regions) {
      branchesParams.mb_regions = params.mb_regions;
      citiesParams.mb_regions = params.mb_regions;
      plotsParams.mb_regions = params.mb_regions;
      districtsParams.mb_regions = params.mb_regions;
    }

    if (params.branches_ids) {
      citiesParams.mb_ids = params.branches_ids;
      plotsParams.mb_ids = params.branches_ids;
      districtsParams.mb_ids = params.branches_ids;
    }

    if (params.cities_ids) {
      plotsParams.partnerships_ids = params.cities_ids;
      districtsParams.partnerships_ids = params.cities_ids;
    }

    if (params.districts_ids) {
      plotsParams.districts_ids = params.districts_ids;
    }

    if (params.partnerships_ids) {
      plotsParams.partnerships_ids = params.partnerships_ids;
    }

    const regions = this.getRegions();
    const branches = this.getBranches(branchesParams);
    const cities = this.getCities(citiesParams);
    const plots = this.getFilterPlots(plotsParams);
    const companies = this.companiesService.getCompaniesFilter();
    const districts = this.getDistricts(districtsParams);

    return forkJoin([cities, branches, plots, companies, regions, districts]);
  }

  getMonitoringRegions(params): Observable<CustomHttpResponse<string[]>> {
    return this.http.get<CustomHttpResponse<string[]>>(`/admin/regions`, {
      params,
    });
  }

  getMonitoringBranches(params): Observable<CustomHttpResponse<BranchNames[]>> {
    return this.http.get<CustomHttpResponse<BranchNames[]>>(`/admin/branches/`, { params });
  }

  getMonitoringDistricts(params): Observable<CustomHttpResponse<DistrictNames[]>> {
    return this.http.get<CustomHttpResponse<DistrictNames[]>>(`/admin/districts/`, { params });
  }

  getMonitoringSectors(params): Observable<any> {
    return this.http.get(`/admin/monitoring/sectors/`, { params });
  }

  getMonitoringDataUnion(params) {
    const regions = this.getMonitoringRegions({ available_only: 1 });
    const branches = this.getMonitoringBranches({
      mb_regions: '',
      available_only: 1,
    });
    const sectors = this.getMonitoringSectors(params);
    const sectors_numbers = this.getFilterPlots(null);

    return forkJoin([regions, branches, sectors, sectors_numbers]);
  }

  makeSectorsNumbers(sectors): any[] {
    return orderBy(
      sectors.map((s) => {
        if (!s.is_visible) {
          s.sector_number_str = '<span class="app-combobox__item--highlight-part">' + s.sector_number_str + '</span>';
          s.sector_number_sort = +s.sector_number_str;
        }
        return s;
      }),
      ['sector_number_sort', 'asc']
    );
  }
}
